import * as React from "react";
import {
  getCountryCallingCode,
  isPossiblePhoneNumber,
  isValidPhoneNumber,
  type Country,
} from "react-phone-number-input";
import flags from "react-phone-number-input/flags";
import { Check, ChevronsUpDown, Search } from "lucide-react";
import { cn } from "../../lib/cn";
import { Input } from "../Input";
import { Popover, PopoverContent, PopoverTrigger } from "../Popover";

export const PHONE_COUNTRY_CODES = ["+966", "+20", "+971", "+962"] as const;

export type PhoneCountryCode = (typeof PHONE_COUNTRY_CODES)[number];

export function resolveCountryCode(
  value: string | undefined,
  codes: readonly PhoneCountryCode[] = PHONE_COUNTRY_CODES,
): PhoneCountryCode | undefined {
  if (!value) return undefined;
  const sorted = [...codes].sort((a, b) => b.length - a.length);
  return sorted.find((code) => value.startsWith(code));
}

export type PhoneNumberCheck =
  "valid" | "missing-leading-zero" | "invalid-length" | "invalid";

/**
 * Egypt is written in its local form in the field: the user types the
 * leading 0 ("01012345678", 11 digits) and it is required. The backend
 * stores it without the 0 (`+201012345678`) -- convert with
 * `toPhoneApiValue` / `toPhoneFieldValue` at the form's edges.
 */
const EGYPT_CODE = "+20";

/** An Egyptian number in the field is exactly this long: 0 + 10 digits. */
export const EGYPT_PHONE_DIGITS = 11;

/** A Saudi mobile number in the field is exactly this long: 5 + 8 digits. */
export const SAUDI_PHONE_DIGITS = 9;

/**
 * Checks a PhoneNumberField value (`+<code><digits>`) against the selected
 * country's numbering metadata (libphonenumber) rather than one global
 * length rule: a digit count the country never uses is `invalid-length`; a
 * plausible length that no real number range matches is `invalid`. An
 * Egyptian number typed without its leading 0 is `missing-leading-zero`,
 * and one that isn't exactly 11 digits is `invalid-length`.
 */
export function checkPhoneNumber(value: string): PhoneNumberCheck {
  const trimmed = value.trim();
  if (trimmed.startsWith(EGYPT_CODE)) {
    const national = trimmed.slice(EGYPT_CODE.length);
    if (!national.startsWith("0")) return "missing-leading-zero";
    if (national.length !== EGYPT_PHONE_DIGITS) return "invalid-length";
  }
  const international = toPhoneApiValue(trimmed);
  if (!isPossiblePhoneNumber(international)) return "invalid-length";
  return isValidPhoneNumber(international) ? "valid" : "invalid";
}

/** The field's value as the backend takes it: Egypt's leading 0 dropped. */
export function toPhoneApiValue(value: string): string {
  return value.startsWith(`${EGYPT_CODE}0`)
    ? `${EGYPT_CODE}${value.slice(EGYPT_CODE.length + 1)}`
    : value;
}

/** A stored number as the field shows it: Egypt's leading 0 added back. */
export function toPhoneFieldValue(value: string): string {
  const national = value.startsWith(EGYPT_CODE)
    ? value.slice(EGYPT_CODE.length)
    : "";
  return national !== "" && !national.startsWith("0")
    ? `${EGYPT_CODE}0${national}`
    : value;
}

export function stripCountryCodePrefix(value: string, code: string): string {
  if (value.startsWith(code)) {
    return value.slice(code.length);
  }
  return value;
}

/** Most digits the user can type per country. Egypt counts its leading 0
 *  (0 + 10 = 11); the others drop it and top out at 9. */
export const PHONE_NATIONAL_MAX_DIGITS: Record<PhoneCountryCode, number> = {
  "+966": SAUDI_PHONE_DIGITS,
  "+20": EGYPT_PHONE_DIGITS,
  "+971": 9,
  "+962": 9,
};

/** The number's shape per country -- the leading digit(s) and one X per
 *  remaining digit -- so the hint always matches the length rules above.
 *  The field owns it; screens don't pass their own. */
export const PHONE_PLACEHOLDERS: Record<PhoneCountryCode, string> = {
  "+966": "5XXXXXXXX",
  "+20": "01XXXXXXXXX",
  "+971": "5XXXXXXXX",
  "+962": "7XXXXXXXX",
};

/**
 * Normalizes what the user typed or pasted into the national number the
 * field keeps: digits only, a pasted country code dropped, and capped at the
 * country's length. Outside Egypt a leading 0 is dropped (the country code
 * replaces it); in Egypt it is kept, and a number pasted with +20 gets it.
 */
export function sanitizeNationalNumber(
  input: string,
  code: PhoneCountryCode,
): string {
  const max = PHONE_NATIONAL_MAX_DIGITS[code];
  const callingDigits = code.slice(1);
  let digits = input.replace(/\D/g, "");
  if (code === EGYPT_CODE) {
    // "+20 101...", "0020 101..." or "+20 0101..." pasted whole.
    const prefix = [`00${callingDigits}`, callingDigits].find(
      (candidate) => digits.length > max && digits.startsWith(candidate),
    );
    if (prefix !== undefined) {
      digits = `0${digits.slice(prefix.length).replace(/^0+/, "")}`;
    }
    return digits.slice(0, max);
  }
  digits = digits.replace(/^0+/, "");
  // "+966 50...", "00966..." or "966 050..." pasted whole.
  if (digits.length > max && digits.startsWith(callingDigits)) {
    digits = digits.slice(callingDigits.length).replace(/^0+/, "");
  }
  return digits.slice(0, max);
}

const COUNTRY_TO_CODE: Record<string, PhoneCountryCode> = {
  SA: "+966",
  EG: "+20",
  AE: "+971",
  JO: "+962",
};

const CODE_TO_COUNTRY: Record<PhoneCountryCode, Country> = {
  "+966": "SA",
  "+20": "EG",
  "+971": "AE",
  "+962": "JO",
};

export interface PhoneNumberFieldProps {
  value?: string;
  defaultValue?: string;
  onChange?: (value: string) => void;
  onBlur?: (event: React.FocusEvent<HTMLInputElement>) => void;
  countryNames?: Record<PhoneCountryCode, string>;
  countryIcons?: Partial<Record<PhoneCountryCode, React.ReactNode>>;
  countrySelectAriaLabel?: string;
  defaultCountryCode?: PhoneCountryCode;
  disabled?: boolean;
  error?: boolean;
  required?: boolean;
  id?: string;
  name?: string;
  className?: string;
  containerClassName?: string;
  "aria-describedby"?: string;
  "aria-invalid"?: boolean | "true" | "false";
  ref?: React.Ref<HTMLInputElement>;
}

interface CountrySelectOption {
  label: string;
  value: Country;
  callingCode: string;
}

interface CountrySelectProps {
  disabled?: boolean | undefined;
  value: Country;
  onChange: (value: Country) => void;
  options: CountrySelectOption[];
  ariaLabel?: string | undefined;
  error?: boolean | undefined;
  countryIcons?: Partial<Record<PhoneCountryCode, React.ReactNode>> | undefined;
}

function FlagComponent({
  country,
  countryName,
}: {
  country: Country;
  countryName: string;
}) {
  const Flag = flags[country];
  return (
    <span className="flex h-4 w-6 shrink-0 items-center justify-center overflow-hidden rounded-xs border border-container-default-border bg-muted">
      {Flag ? <Flag title={countryName} /> : null}
    </span>
  );
}

function CountrySelect({
  disabled,
  value,
  onChange,
  options,
  ariaLabel,
  error,
  countryIcons,
}: CountrySelectProps) {
  const [open, setOpen] = React.useState(false);
  const [searchQuery, setSearchQuery] = React.useState("");

  const selectedOption =
    options.find((opt) => opt.value === value) ?? options[0];
  const activeCode = selectedOption
    ? COUNTRY_TO_CODE[selectedOption.value]
    : undefined;

  const filteredOptions = React.useMemo(() => {
    if (!searchQuery.trim()) return options;
    const query = searchQuery.toLowerCase().trim();
    return options.filter(
      (opt) =>
        opt.label.toLowerCase().includes(query) ||
        opt.callingCode.includes(query) ||
        opt.value.toLowerCase().includes(query),
    );
  }, [options, searchQuery]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        role="combobox"
        aria-expanded={open}
        disabled={disabled}
        aria-label={ariaLabel}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown" || e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setOpen(true);
          }
        }}
        {...(error !== undefined ? { error } : {})}
        className={cn(
          "flex h-10 w-[7.125rem] shrink-0 items-center justify-between gap-1.5 rounded-md border border-container-default-border bg-container-default-background px-3 py-2 text-body-s-r text-text transition-colors hover:bg-container-hover-background focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-focus-ring disabled:cursor-not-allowed disabled:opacity-50",
          error && "border-fail-border text-fail-text",
        )}
      >
        <span className="flex items-center gap-1.5 overflow-hidden">
          {activeCode && countryIcons?.[activeCode] ? (
            countryIcons[activeCode]
          ) : selectedOption ? (
            <FlagComponent
              country={selectedOption.value}
              countryName={selectedOption.label}
            />
          ) : null}
          <span dir="ltr" className="font-medium">
            +{selectedOption?.callingCode}
          </span>
        </span>
        <ChevronsUpDown
          className="size-4 shrink-0 text-text-placeholder opacity-60"
          aria-hidden="true"
        />
      </PopoverTrigger>

      <PopoverContent align="start" className="w-72 p-2">
        <div className="mb-2 flex items-center gap-2 rounded-md border border-container-default-border bg-container-default-background px-2.5 py-1.5">
          <Search
            className="size-4 shrink-0 text-text-placeholder"
            aria-hidden="true"
          />
          <input
            type="search"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search country or code..."
            className="w-full border-0 bg-transparent p-0 text-body-s-r text-text placeholder:text-text-placeholder focus:outline-none"
          />
        </div>

        <div className="max-h-60 space-y-0.5 overflow-y-auto">
          {filteredOptions.length === 0 ? (
            <p className="p-2 text-center text-body-s-r text-text-placeholder">
              No country found.
            </p>
          ) : (
            filteredOptions.map((opt) => {
              const isSelected = opt.value === value;
              const optCode = COUNTRY_TO_CODE[opt.value];
              return (
                <button
                  key={opt.value}
                  type="button"
                  role="option"
                  aria-selected={isSelected}
                  className={cn(
                    "flex w-full items-center justify-between gap-2 rounded-md px-2.5 py-2 text-start text-body-s-r text-text transition-colors hover:bg-container-hover-background",
                    isSelected &&
                      "bg-container-selected-background font-medium",
                  )}
                  onClick={() => {
                    onChange(opt.value);
                    setOpen(false);
                    setSearchQuery("");
                  }}
                >
                  <span className="flex min-w-0 items-center gap-2">
                    {optCode && countryIcons?.[optCode] ? (
                      countryIcons[optCode]
                    ) : (
                      <FlagComponent
                        country={opt.value}
                        countryName={opt.label}
                      />
                    )}
                    <span className="truncate">{opt.label}</span>
                  </span>
                  <span className="flex shrink-0 items-center gap-2 text-text-placeholder">
                    <span dir="ltr">+{opt.callingCode}</span>
                    <Check
                      className={cn(
                        "size-4 text-primary-icon",
                        isSelected ? "opacity-100" : "opacity-0",
                      )}
                    />
                  </span>
                </button>
              );
            })
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}

export function PhoneNumberField({
  value: valueProp,
  defaultValue = "",
  onChange,
  onBlur,
  countryNames,
  countryIcons,
  countrySelectAriaLabel = "Select country code",
  defaultCountryCode = "+966",
  disabled = false,
  error,
  className,
  containerClassName,
  id,
  name,
  "aria-describedby": ariaDescribedby,
  "aria-invalid": ariaInvalid,
  ref,
  ...props
}: PhoneNumberFieldProps) {
  const isControlled = valueProp !== undefined;
  const [internalValue, setInternalValue] = React.useState(defaultValue);
  const rawValue = isControlled ? valueProp : internalValue;

  const matchedCode = resolveCountryCode(rawValue);
  const [selectedCode, setSelectedCode] = React.useState<PhoneCountryCode>(
    matchedCode ?? defaultCountryCode,
  );

  const activeCode = matchedCode ?? selectedCode;
  const currentCountry = CODE_TO_COUNTRY[activeCode] ?? "SA";

  React.useEffect(() => {
    if (matchedCode) {
      setSelectedCode(matchedCode);
    }
  }, [matchedCode]);

  const countryOptions = React.useMemo<CountrySelectOption[]>(() => {
    const supportedCodes: PhoneCountryCode[] = ["+966", "+20", "+971", "+962"];
    return supportedCodes.map((code) => {
      const country = CODE_TO_COUNTRY[code];
      const label = countryNames?.[code] ?? code;
      const callingCode = getCountryCallingCode(country);
      return { label, value: country, callingCode };
    });
  }, [countryNames]);

  const handleCountryChange = (newCountry: Country) => {
    const newCode = COUNTRY_TO_CODE[newCountry] ?? "+966";
    setSelectedCode(newCode);
    const nationalNumber = rawValue
      ? sanitizeNationalNumber(
          stripCountryCodePrefix(rawValue, activeCode),
          newCode,
        )
      : "";
    const newValue = `${newCode}${nationalNumber}`;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const nationalDigits = sanitizeNationalNumber(e.target.value, activeCode);
    const newValue = `${activeCode}${nationalDigits}`;
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onChange?.(newValue);
  };

  const nationalNumber = React.useMemo(() => {
    if (!rawValue) return "";
    return stripCountryCodePrefix(rawValue, activeCode);
  }, [rawValue, activeCode]);

  return (
    <div className={cn("flex w-full items-center gap-2", containerClassName)}>
      <CountrySelect
        disabled={disabled}
        value={currentCountry}
        onChange={handleCountryChange}
        options={countryOptions}
        ariaLabel={countrySelectAriaLabel}
        error={error}
        countryIcons={countryIcons}
      />

      <Input
        ref={ref}
        id={id}
        name={name}
        type="tel"
        inputMode="numeric"
        dir="ltr"
        value={nationalNumber}
        onChange={handleNumberChange}
        onBlur={onBlur}
        placeholder={PHONE_PLACEHOLDERS[activeCode]}
        disabled={disabled}
        {...(error !== undefined ? { error } : {})}
        aria-describedby={ariaDescribedby}
        {...(ariaInvalid !== undefined ? { "aria-invalid": ariaInvalid } : {})}
        className={cn("flex-1 text-start", className)}
        {...props}
      />
    </div>
  );
}

export const PhoneInput = PhoneNumberField;
